import React, { type PropsWithChildren, useCallback, useEffect } from 'react';

import { datadog } from '@sg/expo-datadog-client';

import { LoadingAnimation } from '../components';
import { checkIfAllowedHost } from './DomainGuard.utils';

// ─────────────────────────────────────────────────────────────────────────────

export const DomainGuard = (props: PropsWithChildren) => {
  const { children } = props;

  // ─── Derived ───────────────────────────────────────────────────────────────

  const { hostname } = window.location;
  const isAllowedHost = __DEV__ || checkIfAllowedHost(hostname);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const redirectToCanonicalSite = useCallback(() => {
    if (isAllowedHost) {
      return;
    }

    datadog.logger.error('Order web is running on an unknown host', { host: hostname });

    const timeoutId = setTimeout(replaceWithCanonicalUrl, REDIRECT_DELAY_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [hostname, isAllowedHost]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(redirectToCanonicalSite, [redirectToCanonicalSite]);

  if (!isAllowedHost) {
    return <LoadingAnimation />;
  }

  return children;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function replaceWithCanonicalUrl() {
  const { pathname, search, hash } = window.location;

  const canonicalUrl = `${CANONICAL_ORIGIN}${pathname}${search}${hash}`;
  const topWindow = window.top ?? window;

  try {
    topWindow.location.replace(canonicalUrl);
  } catch {
    // Browsers block a cross-origin frame from navigating the top window
    window.location.replace(canonicalUrl);
  }
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CANONICAL_ORIGIN = atob('aHR0cHM6Ly9vcmRlci5zd2VldGdyZWVuLmNvbQ==');
const REDIRECT_DELAY_MS = 1000;
